export const SearchBar = {
	init() {
		document.querySelectorAll( '[data-search-bar]' ).forEach( ( bar ) => {
			this.initBar( bar );
		} );
	},

	initBar( bar ) {
		const fields = bar.querySelectorAll( '[data-search-field]' );

		// Cascading Room Type: map of location-value → allowed room-type option
		// names (rendered server-side onto .search-bar__inputs). Stored on the
		// bar so syncAll can refilter every bar when a location is picked.
		let roomtypeMap = {};
		const inputsEl = bar.querySelector( '.search-bar__inputs' );
		if ( inputsEl && inputsEl.dataset.roomtypeMap ) {
			try { roomtypeMap = JSON.parse( inputsEl.dataset.roomtypeMap ); } catch ( e ) { roomtypeMap = {}; }
		}
		bar._thriveRoomtypeMap = roomtypeMap;

		fields.forEach( ( field ) => {
			const fieldType = field.dataset.searchField;
			const trigger = field.querySelector( '.search-bar__trigger' );
			const dropdown = field.querySelector( '.search-dropdown' );
			if ( ! trigger || ! dropdown ) return;

			const input = dropdown.querySelector( '[data-search-input]' );
			const items = dropdown.querySelectorAll( '.search-dropdown__item' );
			const groups = dropdown.querySelectorAll( '[data-group]' );
			const valueEl = trigger.querySelector( '.search-bar__value' );

			trigger.addEventListener( 'click', () => {
				// Room Type is locked until a Location / Building / University
				// is chosen — route the user to the location field instead.
				if ( 'room' === fieldType && field.classList.contains( 'is-locked' ) ) {
					const locField = bar.querySelector( '[data-search-field="location"]' );
					if ( locField ) {
						locField.classList.add( 'search-bar__field--error' );
						const lt = locField.querySelector( '.search-bar__trigger' );
						const ld = locField.querySelector( '.search-dropdown' );
						this.closeAll( bar );
						if ( lt && ld ) {
							this.open( lt, ld );
							const li = ld.querySelector( '[data-search-input]' );
							if ( li ) li.focus();
						}
					}
					return;
				}

				const isOpen = trigger.getAttribute( 'aria-expanded' ) === 'true';
				this.closeAll( bar );

				if ( ! isOpen ) {
					this.open( trigger, dropdown );
					if ( input ) {
						input.value = '';
						input.focus();
						this.filterItems( '', items, groups );
					}
				}
			} );

			if ( input ) {
				input.addEventListener( 'input', () => {
					this.filterItems( input.value, items, groups );
				} );
			}

			items.forEach( ( item ) => {
				item.addEventListener( 'click', () => {
					const value = item.dataset.value;
					const priceEl = item.querySelector( '.search-dropdown__price' );
					const priceHtml = priceEl ? priceEl.outerHTML : '';

					if ( valueEl ) {
						valueEl.textContent = value;
						valueEl.classList.add( 'search-bar__value--selected', 'has-value' );
					}

					// Show selected price next to value.
					const priceTarget = trigger.querySelector( '.search-bar__selected-price' );
					if ( priceTarget ) {
						priceTarget.innerHTML = priceHtml;
					}

					this.close( trigger, dropdown );
					this.syncAll( fieldType, value, priceHtml );

					// Picking a location refilters the Room Type field on every
					// search bar (hero + sticky header share the selection).
					if ( 'location' === fieldType ) {
						document.querySelectorAll( '[data-search-bar]' ).forEach( ( b ) => {
							this.applyCascadeToBar( b, value );
						} );
					}
				} );
			} );
		} );

		// Initialise the Room Type cascade from any pre-selected location (e.g.
		// on a results page the header bar is pre-filled with the searched s_loc).
		const initLocEl = bar.querySelector( '[data-search-field="location"] .search-bar__value' );
		const initLoc = initLocEl && initLocEl.classList.contains( 'search-bar__value--selected' )
			? initLocEl.textContent.trim()
			: '';
		this.applyCascadeToBar( bar, initLoc );

		document.addEventListener( 'click', ( e ) => {
			if ( ! e.target.closest( '[data-search-field]' ) ) {
				this.closeAll( bar );
			}
		} );

		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' ) {
				this.closeAll( bar );
			}
		} );

		const submitBtn = bar.querySelector( '[data-search-submit]' );
		if ( submitBtn ) {
			submitBtn.addEventListener( 'click', () => {
				const location = bar.querySelector( '[data-search-field="location"] .search-bar__value' );
				const room = bar.querySelector( '[data-search-field="room"] .search-bar__value' );
				// If the user didn't pick a city but the page has implicit
				// location context (e.g. `/short-stays/johannesburg/`), fall
				// back to that — server-rendered into `data-search-default-loc`
				// by template-parts/location-hero.php / header.php / hero.php
				// via thrive_current_location_name(). Avoids dropping the
				// city the user already implied.
				const locExplicit = location?.classList.contains( 'search-bar__value--selected' ) ? location.textContent : '';
				const locVal = locExplicit || ( bar.dataset.searchDefaultLoc || '' ).trim();
				const roomVal = room?.classList.contains( 'search-bar__value--selected' ) ? room.textContent : '';

				// AND logic: a Location / Building / University must be chosen before
				// searching — room type alone is not a valid search. Block submit and
				// nudge the user to the location field instead of running a city-wide
				// room-type query.
				if ( ! locVal ) {
					const locField = bar.querySelector( '[data-search-field="location"]' );
					if ( locField ) {
						locField.classList.add( 'search-bar__field--error' );
						const locTrigger = locField.querySelector( '.search-bar__trigger' );
						if ( locTrigger && locTrigger.getAttribute( 'aria-expanded' ) !== 'true' ) {
							// Open the location dropdown on the next tick. Deferring
							// past this submit click lets the document click-outside
							// handler settle first; routing through the trigger's own
							// click keeps it open (the trigger sits inside
							// [data-search-field], which the outside-click guard ignores).
							setTimeout( () => locTrigger.click(), 0 );
						}
					}
					return;
				}

				// Search mode (`residences` default, `short_stays` on /short-stays/*
				// pages). Server-side helper `thrive_current_search_mode()` sets this
				// data attribute; we echo it back into the URL as `s_mode` so the
				// results template can dispatch to the right CPT + taxonomy.
				const mode = bar.dataset.searchMode || 'residences';

				// Carry over filter params if we're on the search results page.
				const current = new URLSearchParams( window.location.search );
				const params = new URLSearchParams();
				if ( mode && mode !== 'residences' ) params.set( 's_mode', mode );
				if ( locVal ) params.set( 's_loc', locVal );
				if ( roomVal ) params.set( 's_room', roomVal );
				[ 'areas', 'min_price', 'max_price', 'sort' ].forEach( ( key ) => {
					if ( current.has( key ) ) params.set( key, current.get( key ) );
				} );

				const url = window.location.origin + '/?' + params.toString();
				window.location.href = url;
			} );
		}
	},

	syncAll( fieldType, value, priceHtml ) {
		document.querySelectorAll( `[data-search-field="${ fieldType }"]` ).forEach( ( field ) => {
			field.classList.remove( 'search-bar__field--error' );
			const valueEl = field.querySelector( '.search-bar__value' );
			if ( valueEl ) {
				valueEl.textContent = value;
				valueEl.classList.add( 'search-bar__value--selected', 'has-value' );
			}
			const priceTarget = field.querySelector( '.search-bar__trigger .search-bar__selected-price' );
			if ( priceTarget && priceHtml ) {
				priceTarget.innerHTML = priceHtml;
			}
		} );
	},

	// Lock/filter the Room Type field of a bar based on the chosen location.
	// No location → field is locked and any prior room selection is cleared.
	// A location with a known type list → field unlocks and only its types show.
	applyCascadeToBar( bar, locationValue ) {
		const map = bar._thriveRoomtypeMap || {};
		const roomField = bar.querySelector( '[data-search-field="room"]' );
		if ( ! roomField ) return;

		const trigger  = roomField.querySelector( '.search-bar__trigger' );
		const dropdown = roomField.querySelector( '.search-dropdown' );
		const valueEl  = roomField.querySelector( '.search-bar__value' );
		const items    = roomField.querySelectorAll( '.search-dropdown__item' );
		const groups   = roomField.querySelectorAll( '[data-group]' );

		const resetSelection = () => {
			if ( valueEl ) {
				if ( valueEl.dataset.placeholder ) valueEl.textContent = valueEl.dataset.placeholder;
				valueEl.classList.remove( 'search-bar__value--selected', 'has-value' );
			}
			const priceTarget = trigger && trigger.querySelector( '.search-bar__selected-price' );
			if ( priceTarget ) priceTarget.innerHTML = '';
		};

		// Lock ONLY while no location is chosen. Once a Location / Building /
		// University is picked, tapping Room Type must open its OWN panel —
		// otherwise the locked handler bounces back to the Location list (the
		// confusing loop the reviewer hit). When the chosen location has a
		// known type list we filter to it below; when it has no cascade entry
		// (or the map is empty) we still unlock and show every room type.
		if ( ! locationValue ) {
			// Locked.
			roomField.classList.add( 'is-locked' );
			if ( trigger ) {
				trigger.setAttribute( 'aria-disabled', 'true' );
				if ( dropdown ) this.close( trigger, dropdown );
			}
			resetSelection();
			return;
		}

		const allowed = Array.isArray( map[ locationValue ] ) ? map[ locationValue ] : null;

		// Unlocked → filter to the allowed room-type names (when known).
		roomField.classList.remove( 'is-locked' );
		if ( trigger ) trigger.removeAttribute( 'aria-disabled' );

		const currentVal = valueEl && valueEl.classList.contains( 'search-bar__value--selected' )
			? valueEl.textContent.trim()
			: '';
		let currentStillValid = false;

		items.forEach( ( item ) => {
			// No cascade entry for this location → show every room type.
			const show = allowed ? allowed.indexOf( item.dataset.value ) !== -1 : true;
			if ( show ) {
				delete item.dataset.cascadeHidden;
				if ( item.dataset.value === currentVal ) currentStillValid = true;
			} else {
				item.dataset.cascadeHidden = '1';
			}
			item.hidden = ! show;
		} );

		groups.forEach( ( group ) => {
			const visible = group.querySelectorAll( '.search-dropdown__item:not([hidden])' );
			group.hidden = visible.length === 0;
		} );

		// Drop a stale room selection that isn't valid for the new location.
		if ( currentVal && ! currentStillValid ) {
			resetSelection();
		}
	},

	open( trigger, dropdown ) {
		trigger.setAttribute( 'aria-expanded', 'true' );
		dropdown.hidden = false;
	},

	close( trigger, dropdown ) {
		trigger.setAttribute( 'aria-expanded', 'false' );
		dropdown.hidden = true;
	},

	closeAll( bar ) {
		bar.querySelectorAll( '.search-bar__trigger' ).forEach( ( t ) => {
			t.setAttribute( 'aria-expanded', 'false' );
		} );
		bar.querySelectorAll( '.search-dropdown' ).forEach( ( d ) => {
			d.hidden = true;
		} );
	},

	filterItems( query, items, groups ) {
		const q = query.toLowerCase().trim();

		items.forEach( ( item ) => {
			const text = item.dataset.value;
			const textEl = item.querySelector( '.search-dropdown__item-text' );

			// Items hidden by the Room Type cascade stay hidden regardless of
			// the typed query — the location restricts the available types.
			if ( '1' === item.dataset.cascadeHidden ) {
				item.hidden = true;
				return;
			}

			if ( ! q ) {
				item.hidden = false;
				textEl.innerHTML = this.escapeHtml( text );
				return;
			}

			const idx = text.toLowerCase().indexOf( q );
			if ( idx === -1 ) {
				item.hidden = true;
			} else {
				item.hidden = false;
				const before = text.slice( 0, idx );
				const match = text.slice( idx, idx + q.length );
				const after = text.slice( idx + q.length );
				textEl.innerHTML =
					this.escapeHtml( before ) +
					'<mark>' + this.escapeHtml( match ) + '</mark>' +
					this.escapeHtml( after );
			}
		} );

		groups.forEach( ( group ) => {
			const visibleItems = group.querySelectorAll( '.search-dropdown__item:not([hidden])' );
			group.hidden = visibleItems.length === 0;
		} );
	},

	escapeHtml( str ) {
		const div = document.createElement( 'div' );
		div.textContent = str;
		return div.innerHTML;
	},
};
